import { Link } from 'react-router';
import { Icon } from '@/comp/icon/icon';
import TravelTile from '@/comp/travelTile';
import { useCallback, useContext, useMemo } from 'react';
import { affiliate_checkIsAllowToBookService } from '@/_helper/checkAccess';
import * as storage from '@/_helper/storage';
import { AppContext } from '@/context/AppContext';
import { getFlightAlternativeSearchLink, tripStartCountdown } from '@/_helper/bookingDetailsHelper';
import contactModal from '@/comp/overlayModal/contactModal';
import EmergencyContactModal from '@/comp/overlayModal/emergencyContactModal/EmergencyContactModal';
import InfoCard from '@/comp/ui/infoCard/infoCard';
import BookingData, { EmergencyContactData } from '@/model/common/BookingData';

export const ManageBooking = () => {
    const { bookingData } = useContext(AppContext);

    const isServiceAllowed = useCallback((service: string) => {
        if (window.$affiliate && !affiliate_checkIsAllowToBookService(service).includes(service)) {
            return false;
        }
        return true;
    }, []);

    const isFTCRebookingAllowed = useMemo(
        () => window.$allowFlightChangeSearch === 'true' && bookingData?.data?.allowFTCRebooking === true,
        [bookingData?.data?.allowFTCRebooking]
    );

    const isHotelAlternativeSearchAllowed = useMemo(() => {
        const data = bookingData?.data;

        if (!data) {
            return false;
        }

        const isCruiseApis = data.allowedInteractions?.login?.reason === 'cruise-apis';

        return (
            window.$allowHotelChangeSearch === 'true' &&
            !isCruiseApis &&
            !data.frontendParams?.mediatedServices?.accommodation &&
            data.allowHotelRebooking &&
            isServiceAllowed('hotelChangeSearch')
        );
    }, [bookingData?.data, isServiceAllowed]);

    const flightAlternativeSearchLink = useMemo(() => {
        return getFlightAlternativeSearchLink(
            bookingData?.bookingId,
            bookingData,
            affiliate_checkIsAllowToBookService,
            storage
        );
    }, [bookingData]);

    if (!bookingData || !bookingData.data) {
        return null;
    }

    return (
        <div className="tab-content-container">
            {window.$browserInfo.device === 'mobile' && <div className="modal-mobile-background-layer"></div>}
            <div className="tab-header">
                <h3>Buchung</h3>
                {window.$browserInfo.device === 'mobile' && (
                    <Link rel="noopener noreferrer" className="modal-close" to={'/'}>
                        <Icon name="close" ariaLabel="overlay schließen" />
                    </Link>
                )}
            </div>
            <div className="tab-content">
                {isServiceAllowed('flightAlternativeATC') &&
                    bookingData.data.haveToFlightAlternativesFromMail &&
                    bookingData.data?.allowedInteractions?.login?.reason !== 'cruise-apis' && (
                        <FlightAlternativeATC
                            url={
                                window.$searchFrontendUrl +
                                '/flugalternativen' +
                                '?bookingId=' +
                                bookingData?.bookingId +
                                '&travelStart=' +
                                storage.get('travelStart') +
                                '&firstPaxName=' +
                                storage.get('firstPaxName')
                            }
                        />
                    )}
                {isFTCRebookingAllowed && flightAlternativeSearchLink && (
                    <FlightAlternativeSearch
                        url={flightAlternativeSearchLink}
                        needRebooking={
                            bookingData.data?.haveToFTCRebooking ||
                            bookingData.data?.isFlightCancelledInbound ||
                            bookingData.data?.isFlightCancelledOutbound
                        }
                    />
                )}
                {isHotelAlternativeSearchAllowed && (
                    <HotelAlternativeSearch
                        url={
                            window.$searchFrontendUrl +
                            '/search?bookingId=' +
                            bookingData.bookingId +
                            '&travelStart=' +
                            storage.get('travelStart') +
                            '&firstPaxName=' +
                            storage.get('firstPaxName') +
                            '&hash=' +
                            storage.get('access_token') +
                            '&step=hotel'
                        }
                    />
                )}

                {isServiceAllowed('changeSeatReservationService') &&
                    bookingData.data.serviceInformation.bookedSeats && <ChangeSeatReservation />}

                {isServiceAllowed('changeExcursionService') &&
                    bookingData.data.serviceInformation.excursions &&
                    window.$content.extras.excursion?.active && <ChangeExcursion />}

                {isServiceAllowed('emergencyContact') &&
                    window.$allowEmergencyContact === 'true' &&
                    !window.deactivateServices.includes('EmergencyContact') &&
                    bookingData?.data?.allowedInteractions?.login?.reason !== 'cruise-apis' && (
                        <EmergencyContact
                            emergencyContactData={
                                !Array.isArray(bookingData.data.emergencyContactData)
                                    ? bookingData.data.emergencyContactData
                                    : undefined
                            }
                        />
                    )}

                {isServiceAllowed('apis') && (bookingData.data.allowApisData || bookingData.data.gotApisData) && (
                    <NeedApisData data={bookingData.data} />
                )}

                {bookingData?.data?.allowedInteractions?.login?.reason !== 'cruise-apis' && (
                    <div className="info-text">
                        Zusatzleistungen für Ihre Reise finden Sie{' '}
                        <Link rel="noopener noreferrer" className="link" to={'/zusatzleistung'}>
                            hier
                        </Link>
                        .
                    </div>
                )}
            </div>
        </div>
    );
};

const FlightAlternativeATC = ({ url }: { url: string }) => {
    // trigger linkout after redirect
    if (
        sessionStorage.getItem('linkOutTrigger') !== undefined &&
        sessionStorage.getItem('linkOutTrigger') === 'flightAlternativeATC'
    ) {
        sessionStorage.removeItem('linkOutTrigger');

        window.open(url, '_blank');
    }

    return (
        <TravelTile
            name="flight"
            icon="flight"
            headline="Flugalternative auswählen"
            description="Hier können Sie einen anderen Flug aus der Ihnen zugesandten <span class='noLineBreak'>E-Mail</span> auswählen."
            state="alert"
            link={url}
            linkType="external"
            target="_blank"
        />
    );
};

const FlightAlternativeSearch = ({ url, needRebooking }: { url: string; needRebooking: boolean }) => {
    // trigger linkout after redirect
    if (
        sessionStorage.getItem('linkOutTrigger') !== undefined &&
        sessionStorage.getItem('linkOutTrigger') === 'flightChangeSearch'
    ) {
        sessionStorage.removeItem('linkOutTrigger');

        window.open(url, '_blank');
    }

    return (
        <TravelTile
            name="flight"
            icon="flight"
            headline="Flug ändern"
            description={
                needRebooking
                    ? 'Hier müssen Sie Ihren bisherigen Flug ändern und eine Alternative auswählen.'
                    : 'Bei Bedarf können Sie Ihren bisherigen Flug hier ändern und eine Alternative auswählen.'
            }
            state={needRebooking ? 'alert' : 'show'}
            link={url}
            linkType="external"
            target="_blank"
        />
    );
};

const HotelAlternativeSearch = ({ url }: { url: string }) => {
    // trigger linkout after redirect
    if (
        sessionStorage.getItem('linkOutTrigger') !== undefined &&
        sessionStorage.getItem('linkOutTrigger') === 'hotelChangeSearch'
    ) {
        sessionStorage.removeItem('linkOutTrigger');

        window.open(url, '_blank');
    }

    return (
        <TravelTile
            name="accommodation"
            icon="locationAway"
            headline="Unterkunft ändern"
            description="Auf Wunsch können Sie hier eine neue Unterkunft für Ihre Reise auswählen."
            state="show"
            link={url}
            linkType="external"
            target="_blank"
        />
    );
};

const ChangeSeatReservation = () => (
    <TravelTile
        name="changeSeatReservation"
        icon="airlineSeat"
        headline="Sitzplatzreservierung (Flug) ändern"
        description="Bei Bedarf können Sie Ihre Sitzplatzreservierung im Flugzeug  über den AurumTours Service ändern."
        state="show"
        linkType="modal"
        target="_blank">
        {contactModal()}
    </TravelTile>
);

const ChangeExcursion = () => (
    <TravelTile
        name="changeExcursion"
        icon="attractions"
        headline="Ausflug ändern"
        description="Auf Wunsch können Sie Ihren gebuchten Ausflug über den AurumTours Service ändern."
        state="show"
        linkType="modal"
        target="_blank">
        {contactModal()}
    </TravelTile>
);

const EmergencyContact = ({ emergencyContactData }: { emergencyContactData?: EmergencyContactData }) => {
    const registered = emergencyContactData?.name !== undefined && emergencyContactData?.name !== null;

    return (
        <TravelTile
            name="emergency-contact"
            icon="emergency"
            headline={registered ? 'Notfallkontakt' : 'Notfallkontakt hinzufügen'}
            description={
                registered
                    ? 'Sie haben Ihren Notfallkontakt bereits hinzugefügt. Hier können Sie diesen ändern.'
                    : 'Hinterlegen Sie Ihre Kontaktnummer, damit wir Sie im Notfall schnellstmöglich erreichen können.'
            }
            state={registered ? 'editable' : 'show'}
            link="notfall-kontakt"
            linkType={registered ? 'modal' : 'internal'}
            target="_blank">
            {registered && <EmergencyContactModal contactData={emergencyContactData} />}
        </TravelTile>
    );
};

const NeedApisData = ({ data }: { data: BookingData['data'] }) => {
    let apisState = { state: 'show', linkType: 'internal' };

    if (data.gotApisData) {
        if (data.frontendParams.apis) {
            apisState.state = 'read-only';
            apisState.linkType = 'modal';
        } else {
            apisState.state = 'is-already-booked';
        }
    } else if (data.allowApisData) {
        if (data.alertApisData) {
            apisState.state = 'alert';
        }
    }

    if (data.allowApisData === true && data.gotApisData !== undefined) {
        const tripStartCountdownValue = tripStartCountdown(data.searchParams?.depDate);
        if (
            data.allowApisData &&
            data.gotApisData === false &&
            tripStartCountdownValue &&
            tripStartCountdownValue < 3
        ) {
            return (
                <InfoCard type="large">
                    <div>
                        Da Ihr Abflug in Kürze stattfindet, können Sie leider keine Reisepassdaten mehr über den
                        AurumTours Kundenbereich übermitteln.
                        <br />
                        <br />
                        Sie können die Daten aber auch vor Ort beim Check-In an die durchführende Fluggesellschaft
                        durchgeben.
                    </div>
                </InfoCard>
            );
        } else {
            return (
                <TravelTile
                    icon="airplaneTicket"
                    name="apis"
                    headline={data.gotApisData ? 'Reisepassdaten' : 'Reisepassdaten hinterlegen'}
                    description={
                        data.gotApisData
                            ? 'Sie haben Ihre Reisepassdaten bereits eingetragen.'
                            : 'Bitte tragen Sie hier Ihre Reisepassdaten ein.'
                    }
                    state={apisState.state}
                    link="apis"
                    linkType={apisState.linkType}
                    target="_blank">
                    {data.gotApisData && data.frontendParams.apis && (
                        <>
                            <div className="banner">
                                <p>
                                    Die übermittelten Daten stimmen nicht? Dann melden Sie sich bitte beim AurumTours
                                    Serviceteam unter <a href="mailto:service@aurumtours.de">service@aurumtours.de</a>.
                                </p>
                            </div>
                            {Object.keys(data.frontendParams.apis).map((value, index) => {
                                const apisData = data.frontendParams.apis?.[value];
                                if (apisData) {
                                    return (
                                        <div className={index % 2 === 0 ? 'left' : 'right'} key={'apis-' + index}>
                                            <div className="detail-item">
                                                <Icon
                                                    name="airplaneTicket"
                                                    size={28}
                                                    className="color-primary"
                                                    aria-hidden={true}
                                                />
                                                <div className="e">
                                                    <h4>
                                                        Reisepassdaten für{' '}
                                                        {apisData['vollständiger Name lt. Reisepass']}
                                                    </h4>
                                                    <div className="seg">
                                                        <div>Name</div>
                                                        <div>{apisData['vollständiger Name lt. Reisepass']}</div>
                                                    </div>
                                                    <div className="seg">
                                                        <div>Geburtsdatum</div>
                                                        <div>{apisData['Geburtsdatum']}</div>
                                                    </div>
                                                    <div className="seg">
                                                        <div>Geschlecht</div>
                                                        <div>
                                                            {apisData['Geschlecht'] === 'M' ? 'männlich' : 'weiblich'}
                                                        </div>
                                                    </div>
                                                    <div className="seg">
                                                        <div>Aussstellungsland des Reisepasses</div>
                                                        <div>{apisData['Ausstellungsland Reisepass']}</div>
                                                    </div>
                                                    <div className="seg">
                                                        <div>Staatsangehörigkeit</div>
                                                        <div>{apisData['Nationalität']}</div>
                                                    </div>
                                                    <div className="seg">
                                                        <div>Reisepassnummer</div>
                                                        <div>{apisData['Reisepassnummer']}</div>
                                                    </div>
                                                    <div className="seg">
                                                        <div>Gültig bis</div>
                                                        <div>{apisData['Ablaufdatum Reisepass']}</div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                }
                            })}
                        </>
                    )}
                </TravelTile>
            );
        }
    }
};
